import {
  STATUSES,
  MIN_SQFT,
  MAX_SQFT,
  MIN_ACRES,
  MAX_ACRES,
  MIN_PRICE,
  MAX_PRICE,
  HOME_TYPES,
  MAX_YEAR_BUILT,
  MIN_YEAR_BUILT,
  PROPERTY_TYPES,
  LISTING_STATUSES,
  MIN_BEDROOMS_COUNT,
  MIN_BATHROOMS_COUNT,
  HAS_SELLER_CONCESSION,
  IS_ASSUMABLE,
  DOWN_PAYMENT,
  MIN_MONTHLY_PAYMENT,
  MAX_MONTHLY_PAYMENT,
  HAS_OPEN_HOUSES
} from '../../../constants/listings'
import history from '../../../history'
import { Form } from '../..'
import omitBy from 'lodash/omitBy'
import pick from 'lodash/pick'
import { useSelector } from 'react-redux'
import { FormEvent, useState } from 'react'
import { FiltersHeader } from './FiltersHeader'
import { defineMessages, useIntl } from 'react-intl'
import { PATH_HELPERS } from '../../../constants/navigation'
import { FiltersFormData } from '../../../types/filtersTypes'
import { selectFilter } from '../../../store/selectors/filters'
import { ListingStatusEnum } from '../../../types/listingStatus'
import { FiltersFooterButtons } from './FiltersFooterButtons'
import { Price } from './Price'
import { MonthlyPayment } from './MonthlyPayment'
import { Rooms } from './Rooms'
import DollarInput from '../../shared/Input/DollarInput'
import { HomeType } from './HomeType'
import { selectCustomerId, selectCustomerProfile } from '../../../store/selectors/customerProfile'
import { LotSize } from './LotSize/LotSize'
import { BuildingSize } from './BuildingSize'
import { ListingStatus } from './ListingStatus'
import { ConstructionYear } from './ConstructionYear'
import { constructFilters, constructFiltersAndOmitEmpty } from '../../../helpers/listings/filters'
import { TrackingEventConstants } from '../../../constants/tracking'
import { RootState } from '../../../types/rootState'
import { useTrackingContext } from '../../../providers/tracking'
import styles from './Filters.module.scss'
import { FinancialFactors } from './FinancialFactors/FinancialFactors'
import { camelizeKeys } from '../../../helpers/utils'
import qs from 'qs'
import { Tours } from './Tours/Tours'

export const MSG = defineMessages({
  bed: {
    id: 'HomeSearch.listings.filters.beds.heading',
    defaultMessage: 'Beds'
  },
  bath: {
    id: 'HomeSearch.listings.filters.baths.heading',
    defaultMessage: 'Baths'
  }
})

export interface FiltersProps {
  onClose: () => void
}

export const Filters = ({ onClose }: FiltersProps) => {
  const cp = useSelector(selectCustomerProfile)
  const FIELDS = [
    MIN_YEAR_BUILT,
    MAX_YEAR_BUILT,
    MIN_BEDROOMS_COUNT,
    MIN_BATHROOMS_COUNT,
    HAS_OPEN_HOUSES,
    HAS_SELLER_CONCESSION,
    cp?.assumableMortgagesEnabled ? IS_ASSUMABLE : undefined,
    ListingStatusEnum.ACTIVE,
    ListingStatusEnum.PENDING,
    ListingStatusEnum.COMING_SOON,
    ListingStatusEnum.ACTIVE_UNDER_CONTRACT,
    ListingStatusEnum.CLOSED,
    { name: MIN_ACRES, parse: LotSize.parseValue },
    { name: MAX_ACRES, parse: LotSize.parseValue },
    { name: MIN_SQFT, parse: BuildingSize.parseValue },
    { name: MAX_SQFT, parse: BuildingSize.parseValue },
    { name: MIN_PRICE, parse: DollarInput.parseValue },
    { name: MAX_PRICE, parse: DollarInput.parseValue },
    { name: MIN_MONTHLY_PAYMENT, parse: DollarInput.parseValue },
    { name: MAX_MONTHLY_PAYMENT, parse: DollarInput.parseValue },
    { name: DOWN_PAYMENT, parse: DollarInput.parseValue }
  ].filter(Boolean)

  const intl = useIntl()
  const filters = useSelector(selectFilter)
  const { trackingClient } = useTrackingContext()
  const customerId = useSelector(selectCustomerId)
  const isPublic = useSelector<RootState, boolean>(state => state.auth.isPublic)
  const [homeTypes, setHomeTypes] = useState<string[]>(filters.propertyTypes ?? [])
  const queryParams = camelizeKeys(
    qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    })
  )

  const handleSubmit = (_: FormEvent, data: FiltersFormData) => {
    const statuses = omitBy(pick(data, LISTING_STATUSES), value => !value)

    data[PROPERTY_TYPES] = homeTypes
    data[STATUSES] = Object.keys(statuses) as ListingStatusEnum[]

    const parsedData = constructFilters(data)

    if (parsedData.minPrice !== filters.minPrice) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_PRICE_MIN
      })
    }

    if (parsedData.maxPrice !== filters.maxPrice) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_PRICE_MAX
      })
    }

    if (parsedData.minMonthlyPayment !== filters.monthlyExpensesInput?.minMonthlyExpenses) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_MONTHLY_PAYMENT_MIN
      })
    }

    if (parsedData.maxMonthlyPayment !== filters.monthlyExpensesInput?.maxMonthlyExpenses) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_MONTHLY_PAYMENT_MAX
      })
    }

    if (parsedData.minBedroomsCount !== filters.minBedroomsCount) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_BED_MIN
      })
    }

    if (parsedData.minBathroomsCount !== filters.minBathroomsCount) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_BATH_MIN
      })
    }

    if (parsedData.minYearBuilt !== filters.minYearBuilt) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_YR_MIN
      })
    }

    if (parsedData.maxYearBuilt !== filters.maxYearBuilt) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_YR_MAX
      })
    }

    if (parsedData.minSqft !== filters.minSqft) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_SQFT_MIN
      })
    }

    if (parsedData.maxSqft !== filters.maxSqft) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_SQFT_MAX
      })
    }

    if (parsedData.minAcres !== filters.minAcres) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_ACRES_MIN
      })
    }

    if (parsedData.maxAcres !== filters.maxAcres) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_ACRES_MAX
      })
    }

    if (parsedData.hasSellerConcession !== filters.hasSellerConcession) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_HAS_SELLER_CONCESSION
      })
    }

    if (parsedData.isAssumable !== filters.isAssumable) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_IS_ASSUMABLE
      })
    }

    if (parsedData.hasOpenHouses !== filters.hasOpenHouses) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_HAS_OPEN_HOUSES
      })
    }

    const dataPropertyTypes = JSON.stringify(data.propertyTypes.sort())
    const filterPropertyTypes = JSON.stringify(filters.propertyTypes.sort())
    if (dataPropertyTypes !== filterPropertyTypes) {
      trackingClient?.searchModified({
        search_filter_added: TrackingEventConstants.FILTER_PROPERTY_TYPE
      })
    }

    const listingsUrl = PATH_HELPERS.listings.buildPath(
      customerId,
      queryParams?.placeId ? 'place_id' : filters.location.level,
      queryParams?.placeId ? queryParams?.placeId : filters.location.name,
      constructFiltersAndOmitEmpty(data)
    )
    const publicListingsUrl = PATH_HELPERS.publicListings.buildPath(
      queryParams?.placeId ? 'place_id' : filters.location.level,
      queryParams?.placeId ? queryParams?.placeId : filters.location.name,
      constructFiltersAndOmitEmpty(data)
    )

    history.push(isPublic ? publicListingsUrl : listingsUrl)

    onClose()
    document.getElementById('list-top')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }

  return (
    <Form className={styles.form} onSubmit={handleSubmit} fields={FIELDS}>
      <FiltersHeader />
      <MonthlyPayment />
      <Price />
      <Tours />
      <FinancialFactors />
      <Rooms name={MIN_BEDROOMS_COUNT} title={intl.formatMessage(MSG.bed)} />
      <Rooms name={MIN_BATHROOMS_COUNT} title={intl.formatMessage(MSG.bath)} />
      <HomeType selectedHomeTypes={homeTypes} onSetSelectedHomeTypes={setHomeTypes} />
      <BuildingSize />
      <LotSize />
      <ConstructionYear />
      <ListingStatus />
      <FiltersFooterButtons onClose={onClose} />
    </Form>
  )
}
